import axios from 'axios';
import ApiError, {ApiIsLoginError, NotLoginCode, isNotLoginBody} from './cms-errors'
import {requestWithRetry} from './cms-http-retry'
import {notifySessionExpired} from './session-expired'
import {
  ERROR_CODE_LOCK_TIMEOUT,
  createApiError,
  createTimeoutError,
  getErrorCode,
  isTimeoutHttpStatus,
  saveRetryConfig,
} from './cms-api-error'

// ⚠ ログイン中かを api_key Cookie で先に判定しないこと（以前は各関数の頭で
//   cookies.get('api_key') が無ければ "not login" を投げていた）。
//   api_key は HttpOnly になり JS から読めない（cms-client-admin の login.go Callback）。
//   未ログインは BFF が 401 の JSON（error.not_login）で返す。

// 401 の未ログイン（使用中のログイン切れ）を AuthProvider に知らせる。
// ログイン中でなければ AuthProvider が無視する（ログイン画面での /api/user の 401 等）。
const reportIfNotLogin = (error) => {
  if (error?.response?.status === 401 && isNotLoginBody(error.response.data)) {
    notifySessionExpired();
  }
};

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    reportIfNotLogin(error);
    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------
// 再試行用の素のインスタンス
// ----------------------------------------------------------------------
// 上の axiosInstance のインターセプタは失敗時に error.response.data だけを
// 残すため、HTTP ステータスが消えて「401 は再試行しない / 5xx は再試行する」
// の判定ができない。再試行が要る httpGet / httpPost はインターセプタなしの
// こちらを使い、最後に同じ変換をこの層でかける (throw する値は従来と同じ)。
// auth-provider.js / page_meta.js が使う default export は据え置き。
const retryAxios = axios.create();

// 送信 1 回ぶん。requestWithRetry から呼ばれる。
const sendRaw = (config) => retryAxios.request(config);

// 従来の response インターセプタと同じ変換（ログイン切れの通知も同じ）。
const toLegacyError = (error) => {
  reportIfNotLogin(error);
  return (error.response && error.response.data) || 'Something went wrong';
};

async function sendWithRetry(config) {
  try {
    return await requestWithRetry(sendRaw, config, {
      onRetry: ({url, attempt, maxRetries, delay}) => {
        console.warn(`[cms-axios] retry ${attempt}/${maxRetries} in ${delay}ms: ${url}`);
      },
    });
  } catch (error) {
    throw toLegacyError(error);
  }
}

// ----------------------------------------------------------------------

export async function httpGet(url, params = {}, headers = {}, prefix = "/api", isBlob = false) {
  // ⚠ httpGet は **再試行しない**。cmsIdempotentRead を付けないため。
  //   GET は冪等だが、504 を返している後端に投げ足すと症状が悪化する
  //   (/user と /config-api/site-admin-config で実際に 504 を観測)。
  //   間欠 500 で画面が空になるのを防ぎたい一覧取得は httpGetRead を使う。
  const config = {
    method: 'get',
    url: `${prefix}${url}`,
    params,
    headers,
  };

  if (isBlob) {
    config.responseType = 'blob';
  }

  const response = await sendWithRetry(config);

  if (isBlob) {
    return response.data; // For blob responses, return the raw data
  }

  const {status, error} = response.data;

  if (status !== 'OK') {
    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }
    // ⚠ error は {コード: メッセージ} のことが多く、そのまま new Error に渡すと "[object Object]" になる。
    throw createApiError(status, error);
  }

  return response.data
}

// 保存系の送信。失敗の本文（{status, error}）は投げずに返し、
// 408 / 504（BFF のタイムアウト）は TIMEOUT の Error にして投げる。
// 再試行は requestWithRetry の対象外（cmsIdempotentRead なし）のまま。
async function sendSave(config) {
  try {
    return await requestWithRetry(sendRaw, config, {
      onRetry: ({url, attempt, maxRetries, delay}) => {
        console.warn(`[cms-axios] retry ${attempt}/${maxRetries} in ${delay}ms: ${url}`);
      },
    });
  } catch (error) {
    reportIfNotLogin(error);
    const httpStatus = error?.response?.status;
    // ⚠ 408 / 504 の本文は純文本 "time out"。後端は実行済みかもしれないので再試行しない。
    if (isTimeoutHttpStatus(httpStatus)) throw createTimeoutError(httpStatus);
    const body = error?.response?.data;
    // 401 の未ログインは従来どおり本文をそのまま投げる（通知は上の reportIfNotLogin で済んでいる）。
    if (isNotLoginBody(body)) throw body;
    if (body && typeof body === 'object' && typeof body.status === 'string') {
      return {data: body};
    }
    throw toLegacyError(error);
  }
}

const sleepMs = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

export async function httpPost(url, data = {}, headers = {}, prefix = "/api") {
  // ⚠ httpPost は通信の再試行をしない。cmsIdempotentRead を付けないため、
  //   保存系がここを通っても 5xx や通信断で再送されることは決してない。
  //   副作用のない一覧取得は httpPostRead を使う。
  //   唯一の例外は E000011（ロックを待ちきれず、何も書き込まれていない）で、
  //   2 秒待って 1 回だけ再送する。2 回目も E000011 ならそのまま投げる。
  const config = {
    method: 'post',
    url: `${prefix}${url}`,
    data,
    headers,
  };

  let response;
  for (let attempt = 0; ; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop -- 再送は直列でなければ意味がない
    response = await sendSave(config);

    const {status, error} = response.data || {};
    if (status === 'OK') break;

    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }

    if (attempt > 0 || getErrorCode(error) !== ERROR_CODE_LOCK_TIMEOUT) {
      throw createApiError(status, error);
    }
    // eslint-disable-next-line no-await-in-loop
    await sleepMs(saveRetryConfig.lockTimeoutDelayMs);
  }

  if ('cms-site-id' in headers) {
    if (url === '/post/create'
      || url === '/post/update'
      || url === '/post/delete'
      || url === '/post/change_rank'
      || url === 'post/change_top_last_rank'
      || url === '/post/type/create'
      || url === '/post/type/update'
      || url === '/post/type/delete'
      || url === '/category/create'
      || url === '/category/update'
      || url === '/category/delete'
      || url === '/category/rank/update'
      || url === '/page/create'
      || url === '/page/update'
      || url === '/page/delete'
      || url === '/page/post/create'
      || url === '/page/post/update'
    ) {
      pageCacheMasterVersionUpdate(headers['cms-site-id']).then({});
    }
  }

  return response.data
}

// ----------------------------------------------------------------------
// status が OK でなくても応答をそのまま返す POST（保存系）
// ----------------------------------------------------------------------
// httpPost は status !== 'OK' のとき error をメッセージだけの Error にして投げる。
// エラーの**コード**で分岐したい保存（管理画面設定の U000132 = 他の人が保存済み）は
// それだと判定できないので、こちらで応答を受け取り、呼び出し側で扱う。
//
// ⚠ 再試行しない・未ログインは httpPost と同じく ApiError で投げる。
//   通信エラーも同じ変換（toLegacyError）で投げる。
//   httpPost の保存後の後処理（ページキャッシュの版上げ）は持たない —— 使うのは
//   /config-api の設定保存だけで、あちらの対象 URL には入っていない。
export async function httpPostResult(url, data = {}, headers = {}, prefix = "/api") {
  let body;
  try {
    const response = await sendWithRetry({
      method: 'post',
      url: `${prefix}${url}`,
      data,
      headers,
    });
    body = response.data;
  } catch (e) {
    // ⚠ サーバは USER_ERROR 等を 2xx 以外の HTTP ステータスで返すことがあり、
    //   そのとき axios は reject し、sendWithRetry は応答本文をそのまま投げる。
    //   本文（status を持つ JSON）なら投げずに返す —— ここはそれを読むための口。
    //   2026-09-27 ローカル実機: U000132 が素のオブジェクトのまま画面まで届き、
    //   確認ダイアログではなく赤いエラー表示になった。
    if (!e || typeof e !== 'object' || typeof e.status !== 'string') throw e;
    body = e;
  }

  const {status, error} = body || {};
  if (status !== 'OK' && ApiIsLoginError(error)) {
    const apiError = new ApiError("not login")
    apiError.ErrorCode = NotLoginCode
    throw apiError
  }
  return body
}

// ----------------------------------------------------------------------
// 副作用のない GET（一覧取得）専用
// ----------------------------------------------------------------------
// 間欠 500 で一覧が空になるのを防ぎたいものだけをこちらに寄せる。
// 現在の利用箇所: /form/list_by_page_pkg_code
//   （デザインエディタが起動時に並列で投げる 5 本のうちの GET 1 本）
export async function httpGetRead(url, params = {}, headers = {}, prefix = "/api") {
  const response = await sendWithRetry({
    method: 'get',
    url: `${prefix}${url}`,
    params,
    headers,
    // 冪等な読み取りであることの申告。これが無いと再試行されない。
    cmsIdempotentRead: true,
  });

  const {status, error} = response.data;

  if (status !== 'OK') {
    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }
    // ⚠ error は {コード: メッセージ} のことが多く、そのまま new Error に渡すと "[object Object]" になる。
    throw createApiError(status, error);
  }

  return response.data
}

// ----------------------------------------------------------------------
// 副作用のない POST（一覧取得・検索クエリ）専用
// ----------------------------------------------------------------------
// 後端の一覧 API は POST だが、pkg_code / limit / offset を受けて
// リストを返すだけで後端の状態を変えない。こちらだけ再試行の対象にする。
//
// ⚠ **ここに足すのは「同じリクエストを 3 回投げても後端の状態が変わらない」
//   と確認できたものだけ。** 判断に迷うものは httpPost を使う
//   （再試行されないので安全側に倒れる）。
//   現在の利用箇所: /page/list, /template/list,
//   /template/layout/list, /template/block/list
export async function httpPostRead(url, data = {}, headers = {}, prefix = "/api") {
  const response = await sendWithRetry({
    method: 'post',
    url: `${prefix}${url}`,
    data,
    headers,
    // 冪等な読み取りであることの申告。これが無いと再試行されない。
    cmsIdempotentRead: true,
  });

  const {status, error} = response.data;

  if (status !== 'OK') {
    if (ApiIsLoginError(error)) {
      const apiError = new ApiError("not login")
      apiError.ErrorCode = NotLoginCode
      throw apiError
    }
    // ⚠ error は {コード: メッセージ} のことが多く、そのまま new Error に渡すと "[object Object]" になる。
    throw createApiError(status, error);
  }

  return response.data
}

export function pageCacheMasterVersionUpdate(site_id) {
  return httpPost("/shopify-page-cache-master-version-update", {}, {
    "cms-site-id": site_id,
  }, "");
}
